<template>
  <div>
    <Layout>
      <div class="flex justify-between items-start gap-4">
        <div class="lg:w-2/3">
          <card>
            <header class="font-bold">Visible to Agent</header>
            <p class="text-sm text-gray-500 mb-8">This will be sent to the agent after you save it.</p>

            <div v-if="customFieldsForEntity?.length == 0">
              <QuillInput
                v-model="newPipelineReview.intro_question"
                label="How is the agent and how is their week going so far? Is there anything we can do to help the agent on a personal level?"
                name="intro_question"
              />

              <QuillInput
                v-model="newPipelineReview.top_three_prospects"
                label="Who are the agent's top 3 customers, and what could the agent be doing differently to get each of them into escrow?"
                name="top_three_prospects"
              />

              <QuillInput
                v-model="newPipelineReview.team_leader_suggestions"
                label="What is the agent not getting from us that would help them do their job at the highest possible level?"
                name="team_leader_suggestions"
              />
            </div>
            <div v-else>
              <div v-for="customField in customFieldsForEntity" :key="customField.id" class="sm:col-span-1 mt-4">
                <dt class="text-sm leading-5 font-medium text-gray-500">
                  {{ customField.name }}
                </dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  <ValueViewerAndEditor
                    :custom-field="customField"
                    :custom-field-value="newPipelineReview.customFields.find(f => f.custom_field_id === customField.id)"
                    custom-fieldable-type="PipelineReview"
                    :custom-fieldable-id="1"
                    :method="'edit'"
                    @updated="handleCustomFieldUpdate($event)"
                  />
                </dd>
              </div>
            </div>

            <hr class="my-8 -ml-8 -mr-8" />

            <header class="font-bold">Private</header>
            <p class="text-sm text-gray-500 mb-8">This is not visible to the agent.</p>

            <QuillInput
              v-model="newPipelineReview.team_leader_private_notes"
              label="Your Private Notes"
              name="team_leader_private_notes"
            />

            <label class="mt-4 block text-sm leading-5 font-medium text-gray-700 mb-1">Pipeline Performance</label>
            <PipelinePerformance
              :score="newPipelineReview.score"
              @set-score="score => (newPipelineReview.score = score)"
            />

            <TButton
              label="Save pipeline review"
              color="amber"
              :disabled="!validPipelineReview"
              class="mt-8"
              @click="createPipelineReview"
            >
              <LocalLoadingSpinner />
            </TButton>
          </card>
        </div>

        <div v-if="loaded" class="lg:w-1/3">
          <Card v-if="eventRecording" extra-classes="overflow-visible">
            <div v-if="eventRecording.daily_recording_url" class="px-4 py-5 sm:px-6">
              <video
                class="rounded-md"
                controls
                muted
                poster="https://cdn.prod.website-files.com/65b91c95d87801883ae8bc1c/65b91ee870714b234cc43b39_Dark%20on%20Light%20Logo.svg"
              >
                <source :src="eventRecording.daily_recording_url" type="video/mp4" />
              </video>
              <MenuTabs
                class="mt-6"
                :tabs="['Score', 'Summary', 'Attendees', 'Transcript']"
                @selected-tab="handleSelectedRecordingTab"
              />
              <div v-if="selectedRecordingTab == 'Score'" class="mt-4">
                <div v-for="key of Object.keys(eventRecording.score)" :key="key">
                  <template v-if="key != 'Reasoning' && key != 'Actual Next Steps'">
                    <dt
                      class="mt-2 text-sm leading-5 font-medium text-gray-500 cursor-pointer tooltip tooltip-left"
                      :data-tip="eventRecording.score.Reasoning[key]"
                    >
                      {{ key }}
                    </dt>
                    <dd class="mt-1 text-sm leading-5 text-gray-900">
                      <Progress :progress="eventRecording.score[key]" />
                    </dd>
                  </template>
                </div>
                <template v-if="eventRecording.score['Actual Next Steps']">
                  <dt class="mt-4 text-sm leading-5 font-medium text-gray-500">What are the next steps?</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ eventRecording.score['Actual Next Steps']['Real Estate Agent'] }}
                  </dd>
                </template>
              </div>
              <div v-if="selectedRecordingTab == 'Summary'" class="mt-6 text-sm text-gray-500">
                {{ eventRecording.daily_summary }}
              </div>
              <div v-if="selectedRecordingTab == 'Transcript'" class="mt-6 text-sm text-gray-500">
                {{ eventRecording.daily_transcription }}
              </div>
              <div v-if="selectedRecordingTab == 'Attendees'" class="mt-6 text-sm text-gray-500">
                {{ eventRecording.daily_attendees.join(', ') }}
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 max-w-xl mx-auto px-4 py-5 sm:px-6">
              No recording is available yet. Recordings, summarization, and transcription can take up to an hour to be
              available. If you have not yet enabled conferencing, start automating your event note taking today.
              <router-link class="font-medium text-indigo-600 hover:text-indigo-500" to="/settings/company/scheduling"
                >Enable Conferencing</router-link
              >
            </div>
          </Card>
          <Card v-else>
            <div class="text-sm text-gray-500 max-w-xl mx-auto px-4 py-5 sm:px-6">
              No recording is available yet. Recordings, summarization, and transcription can take up to an hour to be
              available. If you have not yet enabled conferencing, start automating your event note taking today.
              <router-link class="font-medium text-indigo-600 hover:text-indigo-500" to="/settings/company/scheduling"
                >Enable Conferencing</router-link
              >
            </div>
          </Card>
          <Card class="mt-6">
            <div class="px-4 py-5 sm:px-6">
              <div class="flex items-center justify-between">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  {{ teammate.name }}
                </h3>
              </div>
              <div class="mt-4">
                <dt class="text-sm leading-5 font-medium text-gray-500">Email</dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  <a class="text-indigo-600 hover:text-indigo-900" :href="`mailto:${teammate.email}`">{{
                    teammate.email
                  }}</a>
                </dd>
              </div>
              <div class="mt-4">
                <dt class="text-sm leading-5 font-medium text-gray-500">Phone</dt>
                <dd class="mt-1 text-sm leading-5 text-gray-900">
                  <a class="text-indigo-600 hover:text-indigo-900" :href="`tel:${teammate.phone_number}`">{{
                    teammate.phone_number
                  }}</a>
                </dd>
              </div>
              <router-link
                v-if="pipelineReviews?.length > 0"
                target="_blank"
                :to="`/team/${teammate.id}/pipeline_reviews/${previousPipelineReview.id}`"
                class="mt-12 flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >View previous pipeline review</router-link
              >
              <router-link
                :to="`/team/${teammate.id}`"
                class="mt-4 flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >View teammate</router-link
              >
            </div>
          </Card>
          <Card class="mt-6">
            <div class="px-4 py-5 sm:px-6">
              <div class="flex items-center justify-between">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Attachments</h3>
              </div>
              <FileInput
                limits="any file"
                :multiple="true"
                :model-value="newPipelineReview.files"
                accept="*"
                @update:model-value="newPipelineReview.files = $event"
              />
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  </div>
</template>

<script>
import Layout from '../Layout/index.vue';
import Card from '../Card/index.vue';
import MenuTabs from '../shared/MenuTabs.vue';
import Progress from '../shared/Progress.vue';
import PipelinePerformance from '../PipelinePerformance/index.vue';
import QuillInput from '../shared/QuillInput.vue';
import ValueViewerAndEditor from '../custom_fields/ValueViewerAndEditor.vue';

export default {
  components: {
    Layout,
    Card,
    PipelinePerformance,
    QuillInput,
    ValueViewerAndEditor,
    MenuTabs,
    Progress,
  },
  data() {
    return {
      loaded: false,
      pipelineReviews: [],
      teammate: null,
      newPipelineReview: {
        intro_question: '',
        top_three_prospects: '',
        team_leader_suggestions: '',
        team_leader_private_notes: '',
        score: null,
        files: [],
        customFields: [],
      },
      customFieldsForEntity: [],
      eventRecording: null,
      selectedRecordingTab: 'Score',
    };
  },
  computed: {
    validPipelineReview() {
      if (this.customFieldsForEntity?.length == 0) {
        return (
          this.newPipelineReview.intro_question &&
          this.newPipelineReview.top_three_prospects &&
          this.newPipelineReview.team_leader_suggestions &&
          this.newPipelineReview.score
        );
      } else {
        return true;
      }
    },
    previousPipelineReview() {
      return this.pipelineReviews[0];
    },
  },
  async mounted() {
    await this.fetchCustomFieldsForEntity();
    await this.fetchPipelineReviews();
    await this.fetchTeammate();
    await this.fetchEventForPipelineReview();

    this.loaded = true;
  },
  methods: {
    async fetchCustomFieldsForEntity() {
      const res = await fetch(
        `/api/contacts/${this.$route.params.contactId}/pipeline_reviews/custom_fields_for_entity`
      );
      const data = await res.json();
      this.customFieldsForEntity = data.custom_fields_for_entity;
    },
    async fetchPipelineReviews() {
      const res = await fetch(`/api/contacts/${this.$route.params.contactId}/pipeline_reviews`);
      const data = await res.json();

      this.pipelineReviews = data;
    },
    async fetchTeammate() {
      const res = await fetch(`/api/contacts/${this.$route.params.contactId}`);
      const data = await res.json();

      this.teammate = data.record;
    },
    async fetchEventForPipelineReview() {
      const res = await fetch(`/api/contacts/${this.$route.params.contactId}/pipeline_reviews/contact_event`);
      const data = await res.json();

      this.eventRecording = data.record ? (data.record.event_recording ? data.record.event_recording : null) : null;
    },
    async createPipelineReview() {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      let formData = new FormData();
      formData.append('intro_question', self.newPipelineReview.intro_question);
      formData.append('top_three_prospects', self.newPipelineReview.top_three_prospects);
      formData.append('team_leader_suggestions', self.newPipelineReview.team_leader_suggestions);
      formData.append('team_leader_private_notes', self.newPipelineReview.team_leader_private_notes);
      formData.append('score', self.newPipelineReview.score);
      formData.append('custom_fields_and_values[]', JSON.stringify(self.newPipelineReview.customFields));
      for (let i = 0; i < self.newPipelineReview.files?.length; i++) {
        formData.append('files[]', self.newPipelineReview.files[i], self.newPipelineReview.files[i].name);
      }
      const res = await fetch(`/api/contacts/${this.$route.params.contactId}/pipeline_reviews`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      });

      const data = await res.json();
      self.$store.commit('general/hideLocalLoadingSpinner');
      if (data.status != 'error') {
        await this.$router.push(`/team/${this.$route.params.contactId}/pipeline_reviews/${data.record.id}`);
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Pipeline Review Created',
          alertDescription:
            'Your pipeline review was successfully created.  Use the button below to dismiss this alert.',
        });
      } else {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Pipeline Review Error',
          alertDescription: data.message,
        });
      }
    },
    handleCustomFieldUpdate(e) {
      this.newPipelineReview.customFields.push(e);
    },
  },
};
</script>
